import { useEffect } from 'react'

import { FontWeight } from '@/src/styles/theme'
import {
  FinancialAccount,
  TransactionSyncState,
} from '@/reducers/finances/financialAccountsReducer'
import { updateFinancialAccount } from '@/actions/financialAccountActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TypographyStyles } from '@/styles'
import { Accordion, Button, Dialog, Text } from '@/ui'
import { AccountBannerProps } from './AccountBannerCard'

export const InitialStateDialog = ({
  open,
  account,
  onClose,
  setAccountBanner,
}: {
  open: boolean
  account: FinancialAccount
  onClose: () => void
  setAccountBanner: (accountBanner: AccountBannerProps | null) => void
}) => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()

  useEffect(() => {
    if (open) {
      track('viewed review bank account modal')
    }
  }, [open, track])

  return (
    <Dialog open={open}>
      <Dialog.Content>
        <Dialog.Title>Did you want to show this account?</Dialog.Title>

        <Text>
          Please confirm if you want to show or hide transactions from{' '}
          <span style={{ fontWeight: FontWeight.SEMIBOLD }}>
            {account.name} - {account.mask}
          </span>
          .
        </Text>

        <Text
          variant={TypographyStyles.H3}
          style={{ fontWeight: FontWeight.SEMIBOLD }}
        >
          &quot;Show Account&quot;
        </Text>

        <Text>
          Transactions from this account will start showing up in Heard.
        </Text>

        <Text
          variant={TypographyStyles.H3}
          style={{ fontWeight: FontWeight.SEMIBOLD }}
        >
          &quot;Hide Account&quot;
        </Text>

        <Text>Transactions from this account will be hidden in Heard.</Text>

        <Text>
          To unlink the account from Heard, please visit{' '}
          {account.plaidInstitutionName}&apos;s security center.
        </Text>

        <Accordion type="single" variant="text" collapsible>
          <Accordion.Item value="confirm">
            <Accordion.Trigger>Why do I need to confirm?</Accordion.Trigger>

            <Accordion.Content>
              <Text>
                If you choose to &quot;Account&quot;, our bookkeepers will
                assume transactions from this account are business related. If
                this account has mostly personal transactions, we suggest you
                hide it and add business transactions manually.
              </Text>

              <Text>
                Some potential consequences of mixing business and personal
                expenses include unorganized data, and miscalculation in taxes.
              </Text>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>

        <Dialog.Footer>
          <Button
            variant="secondary"
            onClick={async () => {
              onClose()
              await updateFinancialAccount(account.id, {
                isHidden: true,
                transactionSyncState: TransactionSyncState.DISABLED,
              })(dispatch)
              setAccountBanner({ account, type: 'hide' })
              track('clicked review bank account modal button', {
                button: 'hide account',
              })
            }}
          >
            Hide account
          </Button>

          <Button
            variant="secondary"
            onClick={async () => {
              onClose()
              await updateFinancialAccount(account.id, {
                isHidden: false,
                transactionSyncState: TransactionSyncState.ENABLED,
              })(dispatch)
              setAccountBanner({ account, type: 'show' })
              track('clicked review bank account modal button', {
                button: 'show account',
              })
            }}
          >
            Show account
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
